import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { fetchReferrals } from '@/lib/api'
import { Card } from '@/components/Surface'
import { SubPage } from '@/components/SubPage'
import { haptic } from '@/lib/tg'
import { motion, AnimatePresence } from 'motion/react'

export function Referrals() {
  const { data } = useQuery({ queryKey: ['referrals'], queryFn: fetchReferrals })
  const [qr, setQr] = useState('')
  useEffect(() => {
    if (!data?.ref_link) return
    // black-on-white QR — high contrast on a white tile in both themes
    QRCode.toString(data.ref_link, {
      type: 'svg', margin: 0, errorCorrectionLevel: 'M',
      color: { dark: '#111114', light: '#ffffff' },
    }).then(setQr).catch(() => setQr(''))
  }, [data?.ref_link])
  const [copied, setCopied] = useState(false)

  const copyLink = async () => {
    if (!data) return
    haptic('light')
    try {
      await navigator.clipboard.writeText(data.ref_link)
    } catch {
      // ignore
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  const share = () => {
    haptic('medium')
    if (!data) return
    const text = `Использую Цикаду — подключайся по моей ссылке и получи бесплатные дни:\n${data.ref_link}`
    if ((navigator as any).share) {
      ;(navigator as any).share({ title: 'Цикада', text }).catch(() => {})
    } else {
      copyLink()
    }
  }

  return (
    <SubPage title="реф. программа" subtitle="приглашай — получай дни">
      <div className="px-5 space-y-3 stagger">
        {/* Hero stats */}
        <div className="grid grid-cols-3 gap-2" style={{ ['--i' as never]: 0 }}>
          <Stat label="приглашено" value={data?.total_invited ?? 0} accent />
          <Stat label="оплатили" value={data?.total_paid ?? 0} />
          <Stat label="заработано" value={`${data?.total_days_earned ?? 0} дн`} />
        </div>

        {/* Ref link card */}
        <Card className="p-5" style={{ ['--i' as never]: 1 }}>
          <div className="text-[13px] text-[var(--color-muted)]">твоя ссылка</div>
          {qr && (
            <div className="flex justify-center mt-3">
              <div
                className="qr p-3 rounded-[6px] bg-white"
                style={{ width: 168, height: 168 }}
                dangerouslySetInnerHTML={{ __html: qr }}
              />
            </div>
          )}
          <div className="mt-3 num text-[13px] break-all leading-snug font-medium text-center">
            {data?.ref_link.replace('https://', '') ?? '—'}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <motion.button
              onClick={copyLink}
              whileTap={{ scale: 0.97 }}
              className="tap py-3 rounded-[5px] text-[13.5px] font-semibold border border-[var(--color-line)] bg-[var(--color-surface)] text-[var(--color-ink)]"
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={copied ? 'copied' : 'copy'}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                  className="inline-block"
                >
                  {copied ? '✓ скопировано' : 'скопировать'}
                </motion.span>
              </AnimatePresence>
            </motion.button>
            <motion.button
              onClick={share}
              whileTap={{ scale: 0.97 }}
              className="tap py-3 rounded-[5px] text-[13.5px] font-semibold bg-[var(--color-crimson)] text-white"
            >
              поделиться
            </motion.button>
          </div>
        </Card>

        {/* How it works */}
        <div style={{ ['--i' as never]: 2 }} className="rounded-[6px] p-5 bg-[var(--color-blue-soft)] text-[var(--color-blue-ink)]">
          <div className="text-[14px] font-semibold">как это работает</div>
          <div className="text-[13.5px] leading-relaxed mt-2 opacity-90">
            Делишься ссылкой → друг оформляет тариф → <b>+7 дней</b> вам обоим.
            Дни добавляются к подписке автоматически.
          </div>
        </div>

        {/* History */}
        <div style={{ ['--i' as never]: 3 }}>
          <h2 className="display text-[20px] mt-2 mb-3 px-1">история</h2>
          <Card className="overflow-hidden">
            {(data?.events ?? []).map((e, i, arr) => (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.04 }}
                className="px-5 py-4 flex items-center justify-between"
                style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--color-line)' : 'none' }}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[14.5px] font-semibold truncate">{e.username}</div>
                  <div className="num text-[12px] text-[var(--color-muted)] mt-0.5">
                    {formatDate(e.created_at)}
                  </div>
                </div>
                <div className="num text-[14px] font-semibold text-[var(--color-gold-bright)]">
                  +{e.days} дн
                </div>
              </motion.div>
            ))}
            {(!data || data.events.length === 0) && (
              <div className="px-5 py-8 text-center text-[13px] text-[var(--color-muted)]">
                Пока никого не приглашал
              </div>
            )}
          </Card>
        </div>
      </div>
    </SubPage>
  )
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent?: boolean }) {
  return (
    <div className={'card p-4 ' + (accent ? '' : '')}>
      <div className="text-[11px] text-[var(--color-muted)]">{label}</div>
      <div
        className="display text-[24px] mt-2 num"
        style={{ color: accent ? 'var(--color-accent)' : 'var(--color-ink)' }}
      >
        {value}
      </div>
    </div>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}
